/**
 * <shipping-info-component>
 *
 * Renders an estimated-delivery line ("Est. Delivery to 4220: 2 Apr - 6 Apr  Update")
 * and a right-hand "DELIVERY LOCATION" drawer that lets the customer search the bundled
 * Australia Post postcode/suburb list (assets/au-postcodes.json) and pick a delivery area.
 *
 * Data attributes (set from snippets/shipping-info.liquid):
 *   data-postcode       Initial postcode (from the logged-in customer's default address).
 *   data-postcodes-url  asset_url of au-postcodes.json (lazy-loaded on first open).
 *   data-geocode-url    Optional endpoint for "use my current location" (lat/lng -> postcode).
 *   data-min-days       Min business days for the estimate (default 2).
 *   data-max-days       Max business days for the estimate (default 6).
 */
if (!customElements.get('shipping-info-component')) {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const STORAGE_KEY = 'delivery_postcode';
  const MAX_RESULTS = 12;

  class ShippingInfoComponent extends HTMLElement {
    connectedCallback() {
      this.minDays = parseInt(this.dataset.minDays || '2', 10);
      this.maxDays = parseInt(this.dataset.maxDays || '6', 10);
      this.postcodesUrl = this.dataset.postcodesUrl || '';
      this.geocodeUrl = this.dataset.geocodeUrl || '';
      this.estimateUrl = this.dataset.estimateUrl || '';
      this.estimateToken = this.dataset.estimateToken || '';

      this.drawer = this.querySelector('[data-drawer]');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-results]');
      this.message = this.querySelector('[data-message]');
      this.setView = this.querySelector('[data-set]');
      this.unsetView = this.querySelector('[data-unset]');
      this.postcodeEls = this.querySelectorAll('[data-postcode-value]');
      this.datesEls = this.querySelectorAll('[data-dates]');

      this._data = null;
      this._loadingData = null;

      this.querySelectorAll('[data-open]').forEach((el) =>
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );
      this.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        })
      );

      this.input.addEventListener('input', this._debounce(() => this.search(), 150));
      this.results.addEventListener('click', (e) => {
        const item = e.target.closest('[data-pc]');
        if (item) this.select(item.dataset.pc, item.dataset.loc, item.dataset.state);
      });

      const geo = this.querySelector('[data-geolocate]');
      if (geo) geo.addEventListener('click', (e) => {
        e.preventDefault();
        this.useCurrentLocation();
      });

      // Close when the dark backdrop area is clicked.
      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.close();
      });
      this.drawer.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });

      const saved = this._readStored();
      const isLoggedIn = this.dataset.customerLoggedIn === 'true';
      let initialPostcode = '';
      let initialSuburb = '';
      let initialState = '';

      if (isLoggedIn) {
        initialPostcode = this.dataset.postcode || saved.postcode || '';
        initialSuburb = this.dataset.suburb || saved.suburb || '';
        initialState = this.dataset.state || saved.state || '';
      } else {
        initialPostcode = saved.postcode || this.dataset.postcode || '';
        initialSuburb = saved.suburb || this.dataset.suburb || '';
        initialState = saved.state || this.dataset.state || '';
      }

      this.setPostcode(initialPostcode, initialSuburb, initialState, false);
    }

    /* ----------------------------------------------------------- delivery line */

    async setPostcode(postcode, suburb, state, persist) {
      postcode = (postcode || '').trim();
      suburb = (suburb || '').trim();
      state = (state || '').trim();
      this.postcode = postcode;
      this.suburb = suburb;
      this.state = state;

      if (postcode) {
        this.postcodeEls.forEach((el) => (el.textContent = postcode));
        if (this.setView) this.setView.hidden = false;
        if (this.unsetView) this.unsetView.hidden = true;

        if (this.estimateUrl) {
          this.datesEls.forEach((el) => (el.textContent = 'Calculating...'));
          try {
            const range = await this.fetchEstimateFromDataJet(postcode, suburb, state);
            this.datesEls.forEach((el) => (el.textContent = range));
          } catch (e) {
            console.error('[shipping-info] DataJet estimate failed, falling back to local:', e);
            const range = this._estimateRange();
            this.datesEls.forEach((el) => (el.textContent = range));
          }
        } else {
          const range = this._estimateRange();
          this.datesEls.forEach((el) => (el.textContent = range));
        }
      } else {
        if (this.setView) this.setView.hidden = true;
        if (this.unsetView) this.unsetView.hidden = false;
      }

      if (persist && postcode) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ postcode, suburb, state }));
        } catch (e) {
          /* storage unavailable */
        }
      }
    }

    _estimateRange(min = this.minDays, max = this.maxDays) {
      const start = this._addBusinessDays(new Date(), min);
      const end = this._addBusinessDays(new Date(), max);
      return this._fmt(start) + ' - ' + this._fmt(end);
    }

    _addBusinessDays(date, days) {
      const d = new Date(date.getTime());
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    }

    _fmt(d) {
      return d.getDate() + ' ' + MONTHS[d.getMonth()];
    }

    /* ----------------------------------------------------------------- drawer */

    open() {
      this._setMessage('');
      this.input.classList.remove('is-invalid');
      this.input.value = '';
      this.results.innerHTML = '';
      if (typeof this.drawer.showModal === 'function') {
        this.drawer.showModal();
      } else {
        this.drawer.setAttribute('open', '');
      }
      this.loadData();
      document.body.classList.add('shipping-info-open');
    }

    close() {
      this.drawer.classList.add('is-closing');
      const finish = () => {
        this.drawer.classList.remove('is-closing');
        if (typeof this.drawer.close === 'function') {
          this.drawer.close();
        } else {
          this.drawer.removeAttribute('open');
        }
      };
      this.drawer.addEventListener('animationend', finish, { once: true });
      document.body.classList.remove('shipping-info-open');
      // Fallback in case the animation does not fire.
      setTimeout(finish, 320);
    }

    /* ------------------------------------------------------------ data + search */

    loadData() {
      if (this._data || this._loadingData) return this._loadingData || Promise.resolve();
      this._loadingData = fetch(this.postcodesUrl)
        .then((r) => {
          if (!r.ok) throw new Error('Bad response');
          return r.json();
        })
        .then((rows) => {
          this._data = rows;
          return rows;
        })
        .catch(() => {
          this._data = null;
          this._loadingData = null;
          this._showApiError();
        });
      return this._loadingData;
    }

    async search() {
      const q = this.input.value.trim();
      this.input.classList.remove('is-invalid');
      if (q.length < 2) {
        this.results.innerHTML = '';
        this._setMessage('');
        return;
      }

      if (!this._data) await this.loadData();
      if (!this._data) return; // load failed -> API error already shown

      const matches = this._match(q);
      if (matches.length) {
        this._setMessage('');
        this.renderResults(matches);
      } else {
        this.results.innerHTML = '';
        this.input.classList.add('is-invalid');
        const numeric = /^\d+$/.test(q);
        this._setMessage(
          numeric ? 'Please enter a valid postcode.' : 'No suggestions found',
          'invalid'
        );
      }
    }

    _match(q) {
      const lc = q.toLowerCase();
      if (/^\d+$/.test(q)) {
        const qn = parseInt(q, 10);
        return this._data
          .filter((r) => r[0].startsWith(q) || Math.abs(parseInt(r[0], 10) - qn) <= 15)
          .sort((a, b) => {
            // Exact prefix matches first, then nearest postcode.
            const ap = a[0].startsWith(q) ? 0 : 1;
            const bp = b[0].startsWith(q) ? 0 : 1;
            if (ap !== bp) return ap - bp;
            return Math.abs(parseInt(a[0], 10) - qn) - Math.abs(parseInt(b[0], 10) - qn);
          })
          .slice(0, MAX_RESULTS);
      }
      const starts = [];
      const includes = [];
      for (const r of this._data) {
        const loc = r[1].toLowerCase();
        if (loc.startsWith(lc)) starts.push(r);
        else if (loc.includes(lc)) includes.push(r);
        if (starts.length >= MAX_RESULTS) break;
      }
      return starts.concat(includes).slice(0, MAX_RESULTS);
    }

    renderResults(matches) {
      this.results.innerHTML = matches
        .map(
          (r) =>
            '<li><button type="button" class="shipping-info__result" data-pc="' +
            r[0] +
            '" data-loc="' +
            this._esc(r[1]) +
            '" data-state="' +
            this._esc(r[2] || '') +
            '"><span class="shipping-info__result-pc">' +
            r[0] +
            '</span> <span class="shipping-info__result-loc">' +
            this._esc(r[1]) +
            '</span></button></li>'
        )
        .join('');
    }

    select(postcode, suburb = '', state = '') {
      this.setPostcode(postcode, suburb, state, true);
      this.close();
    }

    async fetchEstimateFromDataJet(postcode, suburb, state) {
      const headers = { 'Content-Type': 'application/json' };
      if (this.estimateToken) {
        headers['token'] = this.estimateToken;
      }

      const res = await fetch(this.estimateUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          postcode: postcode || '',
          suburb: suburb || '',
          state: state || ''
        })
      });

      if (!res.ok) {
        throw new Error('DataJet task estimate failed');
      }

      const data = await res.json();

      // Case 1: Pre-formatted date range returned (e.g. "12 Jul - 15 Jul")
      const dates = data.dates || data.estimate || data.estimated_delivery;
      if (dates) return dates;

      // Case 2: Min and Max days returned (e.g. { min_days: 2, max_days: 5 })
      const min = parseInt(data.min_days || data.minDays || data.min_delivery_days, 10);
      const max = parseInt(data.max_days || data.maxDays || data.max_delivery_days, 10);
      if (!isNaN(min) && !isNaN(max)) {
        return this._estimateRange(min, max);
      }

      throw new Error('Estimated dates/days not found in response');
    }

    /* ----------------------------------------------------- use current location */

    useCurrentLocation() {
      if (!('geolocation' in navigator)) {
        this._showApiError();
        return;
      }
      this._setMessage('Detecting your location…', 'loading');
      navigator.geolocation.getCurrentPosition(
        (pos) => this._resolveLocation(pos.coords.latitude, pos.coords.longitude),
        () => this._showApiError(),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    _resolveLocation(lat, lng) {
      // A reverse-geocode endpoint is required to turn coordinates into a postcode.
      // When none is configured we surface the API-issue state.
      if (!this.geocodeUrl) {
        this._showApiError();
        return;
      }
      const url = this.geocodeUrl + (this.geocodeUrl.includes('?') ? '&' : '?') + 'lat=' + lat + '&lng=' + lng;
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error('Bad response');
          return r.json();
        })
        .then((data) => {
          const pc = data && (data.postcode || data.zip);
          if (pc) {
            this.select(String(pc));
          } else {
            this._showApiError();
          }
        })
        .catch(() => this._showApiError());
    }

    _showApiError() {
      this.results.innerHTML = '';
      this._setMessage(
        "We're having trouble loading delivery locations right now. Please try again later.",
        'error'
      );
    }

    /* ----------------------------------------------------------------- helpers */

    _setMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.className = 'shipping-info__message' + (type ? ' shipping-info__message--' + type : '');
    }

    _readStored() {
      try {
        const val = localStorage.getItem(STORAGE_KEY) || '';
        if (val && val.startsWith('{')) {
          return JSON.parse(val);
        }
        return { postcode: val, suburb: '', state: '' };
      } catch (e) {
        return { postcode: '', suburb: '', state: '' };
      }
    }

    _esc(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    _debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('shipping-info-component', ShippingInfoComponent);
}
